// Usage:
// <div data-controller="deployment-status"
//      data-deployment-status-url-value="/status"
//      data-deployment-status-interval-value="10000">

import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export default class extends Controller {
  static values = {
    url: { type: String, default: "/status" },
    interval: { type: Number, default: 10_000 },
  }

  static targets = [
    "version",
    "released",
    "description",
    "sha",
    "message",
    "author",
    "shaLink",
    "checked",
    "newVersionAlert",
    "ping",
  ]

  connect() {
    this.currentVersion = this.hasVersionTarget
      ? this.versionTarget.textContent.trim()
      : null
    if (this.hasReleasedTarget) {
      const raw = this.releasedTarget.textContent.trim()
      const formatted = this.formatDate(raw)
      if (formatted !== raw) this.releasedTarget.textContent = formatted
    }
    if (this.hasCheckedTarget) {
      this.checkedTarget.textContent = new Date().toLocaleTimeString()
    }
    this.triggerPing()
    this.poll()
    this.timerId = setInterval(() => this.poll(), this.intervalValue)
  }

  disconnect() {
    clearInterval(this.timerId)
    clearTimeout(this.pingTimer)
  }

  async poll() {
    try {
      const res = await get(this.urlValue, { responseKind: "json" })
      if (!res.ok) return
      const data = await res.json
      this.update(data)
    } catch {
      // Network error — will retry next interval
    }
  }

  update(data) {
    const newVersion = data.version ?? "—"

    if (
      this.currentVersion &&
      this.currentVersion !== "—" &&
      this.currentVersion !== "unknown" &&
      this.currentVersion !== newVersion
    ) {
      this.showNewVersionAlert()
    }

    this.setText("version", newVersion)
    this.setText("description", data.description ?? "—")
    this.setText("released", this.formatDate(data.released_at))
    this.setText("sha", data.commit?.short_sha ?? "—")
    this.setText("message", this.firstLine(data.commit?.message))
    this.setText("author", data.commit?.author ?? "—")

    if (this.hasShaLinkTarget) {
      const url = data.commit?.url
      if (url) {
        this.shaLinkTarget.href = url
        this.shaLinkTarget.classList.remove("hidden")
      } else {
        this.shaLinkTarget.classList.add("hidden")
      }
    }

    if (this.hasCheckedTarget) {
      this.checkedTarget.textContent = new Date().toLocaleTimeString()
    }

    this.triggerPing()
    this.currentVersion = newVersion
  }

  triggerPing() {
    if (!this.hasPingTarget) return
    const el = this.pingTarget
    el.classList.remove("animate-ping")
    void el.offsetWidth
    el.classList.add("animate-ping")
    clearTimeout(this.pingTimer)
    this.pingTimer = setTimeout(() => el.classList.remove("animate-ping"), 1000)
  }

  showNewVersionAlert() {
    if (this.hasNewVersionAlertTarget) {
      this.newVersionAlertTarget.classList.remove("hidden")
    }
    setTimeout(() => window.location.reload(), 3000)
  }

  setText(targetName, value) {
    const cap = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${cap}Target`]) {
      this[`${targetName}Target`].textContent = value ?? "—"
    }
  }

  firstLine(message) {
    if (!message) return "—"
    return message.split("\n")[0].trim() || "—"
  }

  formatDate(isoString) {
    if (!isoString || isoString === "—") return "—"
    const d = new Date(isoString)
    if (isNaN(d.getTime())) return isoString
    return d.toLocaleString()
  }
}
